import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicSvgComponent } from '@mzic/mzic-components';

@Component({
  selector: 'lib-passcode-step',
  imports: [CommonModule, TranslocoModule, FormsModule, MzicSvgComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './passcode-step.component.html',
  styleUrl: './passcode-step.component.scss',
})
export class PasscodeStepComponent implements OnChanges {
  @Input() error: string | null = null;
  @Input() backoffSeconds = 0;
  @Input() hideBackButton = false;
  @Output() validated = new EventEmitter<string>();
  @Output() back = new EventEmitter<void>();

  passcode = signal('');
  localError = signal(false);
  digits = signal<string[]>(['', '', '', '', '', '']); // 6 digits

  ngOnChanges(): void {
    if (this.error) {
      this.localError.set(true);
      // Auto-clear after showing error
      setTimeout(() => {
        this.clearDigits();
        this.localError.set(false);
      }, 1200);
    }
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      input.value = this.digits()[index];
      return;
    }

    // Update digit
    const newDigits = [...this.digits()];
    newDigits[index] = value.slice(-1); // Only keep last digit
    this.digits.set(newDigits);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`digit-${index + 1}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }

    // Check if all digits filled
    const fullPasscode = newDigits.join('');
    this.passcode.set(fullPasscode);

    if (fullPasscode.length === 6 && !this.isDisabled) {
      this.validatePasscode(fullPasscode);
    } else {
      this.localError.set(false);
    }
  }

  onDigitKeyDown(index: number, event: KeyboardEvent): void {
    // Handle backspace
    if (event.key === 'Backspace') {
      const newDigits = [...this.digits()];

      if (!newDigits[index] && index > 0) {
        // Move to previous input if current is empty
        const prevInput = document.getElementById(`digit-${index - 1}`);
        if (prevInput) {
          (prevInput as HTMLInputElement).focus();
        }
      }

      newDigits[index] = '';
      this.digits.set(newDigits);
      this.passcode.set(newDigits.join(''));
      this.localError.set(false);
    }
  }

  onDigitPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    if (digits.length === 6 && !this.isDisabled) {
      this.digits.set(digits);
      this.passcode.set(digits.join(''));
      this.validatePasscode(digits.join(''));

      // Focus last input
      const lastInput = document.getElementById('digit-5');
      if (lastInput) {
        (lastInput as HTMLInputElement).focus();
      }
    }
  }

  validatePasscode(code: string): void {
    // Emit passcode to parent for server-side validation
    this.localError.set(false);
    this.validated.emit(code);
  }

  clearDigits(): void {
    this.digits.set(['', '', '', '', '', '']);
    this.passcode.set('');
    const firstInput = document.getElementById('digit-0');
    if (firstInput) {
      (firstInput as HTMLInputElement).focus();
    }
  }

  onBack(): void {
    this.back.emit();
  }

  get isDisabled(): boolean {
    return this.backoffSeconds > 0;
  }

  get hasError(): boolean {
    return this.localError() || !!this.error;
  }
}
